"use strict";

/**
 * Client for the DMS sales change feed (`GET sec/developer/sales/changes`).
 *
 * @remarks
 * WEB-4686 (server: WEB-4679). Several users work the DMS Sales pages at once and
 * each screen used to see only its own changes. The Review New Sales page tried to
 * fix that by re-requesting the whole list every 10 s with `response_md5`, but the
 * server still had to build the full page before it could compare the hash, so
 * every open tab cost a full list build every 10 s.
 *
 * The change feed answers "which sales changed since `since`?" from two indexed
 * range reads, so a quiet poll costs a few milliseconds. The contract:
 *
 *  1. Baseline call with no `since` BEFORE loading the list → `server_time` only.
 *  2. Poll with `since` = the previous `server_time`. The answer carries the changed
 *     rows (same format as the list for the same `include`), `removed_sale_ids`,
 *     `changes[]` (who/when per sale) and a new `server_time`.
 *  3. `full_reload_required` (more than 100 changes, or `since` older than 60 min)
 *     → reload the list normally.
 *
 * The server re-reads 15 s before `since`, so the same change can arrive on two
 * consecutive polls — {@link isNewerThanRow} lets a page ignore the repeat.
 *
 * Polling interval: 15 s, the WEB-4679 load-test recommendation (10 s still safe,
 * never below 5 s). Polling is suspended while the browser tab is hidden and resumes
 * with an immediate poll when it becomes visible again.
 */
class SSSaleChangeFeedService {

    /**
     * @param {Object} $timeout       Angular timeout service (keeps the digest in step).
     * @param {Object} SSHttpService  Showsuite HTTP client.
     * @param {Object} SSUserService  Current user (to tell own changes from others').
     * @param {Object} SSUtilService  Date formatting helpers.
     */
    constructor($timeout, SSHttpService, SSUserService, SSUtilService) {
        const self = this;

        self.$timeout = $timeout;
        self.SSHttpService = SSHttpService;
        self.SSUserService = SSUserService;
        self.SSUtilService = SSUtilService;

        self.URL = 'sec/developer/sales/changes';

        /** Poll every 15 s while the page is visible (WEB-4679 recommendation). */
        self.POLL_INTERVAL_MS = 15 * 1000;
    }

    /**
     * Create a poller bound to one page.
     *
     * @param {Object}   options
     * @param {Object}   options.scope      The page controller's `$scope`; polling stops on `$destroy`.
     * @param {Function} options.getParams  Returns the query params besides `since` —
     *                                      `property_id` and the list's own `include`, so the
     *                                      changed rows come back in the list's row format.
     * @param {Function} options.onChanges  Called with the response `data` of every poll that
     *                                      answered successfully (also when nothing changed:
     *                                      `changes` is then empty).
     * @returns {SaleChangePoller}
     * @remarks One poller per page. Call {@link SaleChangePoller#rebase} before every list
     *          load; it (re)starts the polling loop.
     */
    createPoller(options) {
        return new SaleChangePoller(this, options);
    }

    /**
     * Was this change made by someone other than the signed-in user?
     *
     * @param {Object} change  One `changes[]` entry.
     * @returns {boolean} True when the last action has an author who is not the current
     *                    user, or has no author at all (a write that left no history).
     */
    isByAnotherUser(change) {
        const self = this;

        const me = self.SSUserService.getUser();
        const author = change && change.last_action ? change.last_action.created_by : null;

        if (!author) {
            return true;
        }

        return !(me && me.id == author.id);
    }

    /**
     * Is this change newer than what the page already shows for the sale?
     *
     * @param {Object} change  One `changes[]` entry.
     * @param {Object} row     The sale row currently shown (as returned by the list).
     * @returns {boolean}
     * @remarks A sale's "version" is the later of `updated_at` and its newest history
     *          row — the feed also reports history-only actions (the Sales & Payments
     *          AML/CFT review does not touch the sale row). Comparing versions drops the
     *          repeats the server's 15 s overlap re-read produces. Dates are compared as
     *          instants, so the list's and the feed's date formats need not match.
     */
    isNewerThanRow(change, row) {
        const self = this;

        if (!row) {
            return true;
        }

        const changeVersion = self.getChangeVersion(change);

        let rowVersion = self.toMillis(row.updated_at);
        if (row.history && row.history.length) {
            jQuery.each(row.history, function (i, history) {
                rowVersion = Math.max(rowVersion, self.toMillis(history.created_at));
            });
        }

        return changeVersion > rowVersion;
    }

    /**
     * A change's "version": the later of the sale's `updated_at` and its last action.
     *
     * @param {Object} change  One `changes[]` entry.
     * @returns {number} Epoch milliseconds (0 when neither date is present).
     */
    getChangeVersion(change) {
        const self = this;

        return Math.max(
            self.toMillis(change.updated_at),
            self.toMillis(change.last_action ? change.last_action.created_at : null)
        );
    }

    /**
     * "{Name} on {date, time}" for a change, for the "This sale was updated by …" notice.
     *
     * @param {Object} change  One `changes[]` entry.
     * @returns {string} e.g. "Jane Tan on 19/10/2026 03:41 PM" (local time).
     */
    describeChange(change) {
        const self = this;

        const action = change ? change.last_action : null;
        const name = action && action.created_by && action.created_by.display_name
            ? action.created_by.display_name
            : 'another user';
        const at = action && action.created_at ? action.created_at : (change ? change.updated_at : null);

        return name + ' on ' + self.SSUtilService.formatLocalDateTime(at);
    }

    /**
     * The sale behind the first visible popup of a page, if any.
     *
     * @param {Array<{id: string, sale: Function}>} popups  Popup selectors and a getter for
     *                                                      the sale each one acts on.
     * @returns {Object|null} The sale, or null when no listed popup is open.
     */
    findOpenPopupSale(popups) {
        let found = null;

        jQuery.each(popups, function (i, popup) {
            if (jQuery(popup.id).is(':visible')) {
                found = popup.sale() || null;
                return false;
            }
        });

        return found;
    }

    /**
     * Parse a UTC date-time from the API into epoch milliseconds.
     *
     * @param {string|null} value  `Y-m-d H:i:s` (UTC) or ISO-8601.
     * @returns {number} 0 when absent or unparsable.
     */
    toMillis(value) {
        if (!value) {
            return 0;
        }

        const parsed = moment.utc(value);

        return parsed.isValid() ? parsed.valueOf() : 0;
    }
}

/**
 * One page's polling loop over the sales change feed. Created through
 * {@link SSSaleChangeFeedService#createPoller}.
 *
 * @remarks
 * - Exactly one timer is pending at a time; {@link rebase} and every poll cancel the
 *   previous one, so repeated list loads cannot stack up poll loops.
 * - A `generation` counter drops the answer of a poll that was in flight when the
 *   page rebased (property / filter / page change), since its `since` belongs to the
 *   previous list.
 * - A change already delivered is not delivered again ({@link dropRepeats}).
 * - A failed call (endpoint not on this environment yet, 429, network) is silent; the
 *   page simply keeps the list it has and the loop tries again on the next tick.
 */
class SaleChangePoller {

    /**
     * @param {SSSaleChangeFeedService} service
     * @param {Object} options  See {@link SSSaleChangeFeedService#createPoller}.
     */
    constructor(service, options) {
        const self = this;

        self.service = service;
        self.getParams = options.getParams;
        self.onChanges = options.onChanges;

        self.since = null;
        self.timer = null;
        self.generation = 0;
        self.inFlight = false;
        self.stopped = false;

        /** Latest change version delivered per sale id — see {@link dropRepeats}. */
        self.deliveredVersions = {};

        self.visibilityListener = function () {
            if (!document.hidden) {
                self.pollNow();
            }
        };
        document.addEventListener('visibilitychange', self.visibilityListener);

        if (options.scope) {
            options.scope.$on('$destroy', function () {
                self.stop();
            });
        }
    }

    /**
     * Take a fresh baseline (`server_time` with no `since`) and restart the loop.
     *
     * @returns {Promise} Resolves once the baseline call has answered — successfully or
     *                    not — so the caller can load its list right after it.
     * @remarks Call it BEFORE every list load: anything that commits after the baseline
     *          is then picked up by the next poll (the server's 15 s overlap covers a
     *          change racing the list read itself).
     */
    rebase() {
        const self = this;

        self.generation++;
        self.since = null;
        self.cancelTimer();

        const generation = self.generation;

        return self.service.SSHttpService.getAPIRequest(self.service.URL, self.buildParams(null))
            .then(function (response) {
                if (generation !== self.generation || self.stopped) {
                    return;
                }
                if (!(response instanceof Error) && response.data && response.data.server_time) {
                    self.since = response.data.server_time;
                }
                self.scheduleNext();
            });
    }

    /**
     * Poll right away (e.g. the tab came back to the foreground).
     *
     * @returns {void}
     */
    pollNow() {
        const self = this;

        self.cancelTimer();
        self.poll();
    }

    /**
     * Stop polling for good (page left).
     *
     * @returns {void}
     */
    stop() {
        const self = this;

        self.stopped = true;
        self.cancelTimer();
        document.removeEventListener('visibilitychange', self.visibilityListener);
    }

    /**
     * One tick: ask the feed for changes since the last `server_time`.
     *
     * @returns {void}
     * @remarks Skipped while the tab is hidden (the visibility listener resumes it). With
     *          no baseline yet — the baseline call failed — the tick retries the baseline
     *          instead, so a transient error does not end polling for the session.
     */
    poll() {
        const self = this;

        self.timer = null;

        if (self.stopped || document.hidden) {
            return;
        }
        if (self.inFlight) {
            self.scheduleNext();
            return;
        }
        if (!self.since) {
            self.rebase();
            return;
        }

        const generation = self.generation;
        self.inFlight = true;

        self.service.SSHttpService.getAPIRequest(self.service.URL, self.buildParams(self.since))
            .then(function (response) {
                self.inFlight = false;

                if (generation !== self.generation || self.stopped) {
                    return;
                }

                if (!(response instanceof Error) && response.data && response.data.server_time) {
                    self.since = response.data.server_time;
                    response.data.changes = self.dropRepeats(response.data.changes);
                    try {
                        self.onChanges(response.data);
                    } catch (ex) {
                        console.log(ex);
                    }
                }

                // onChanges() may have rebased (silent list reload) — that already
                // scheduled the next tick for the new generation.
                if (generation === self.generation) {
                    self.scheduleNext();
                }
            });
    }

    /**
     * Drop changes this poller already delivered, and remember the rest.
     *
     * @param {Array|undefined} changes  `changes[]` of a poll answer.
     * @returns {Array} The changes not delivered before.
     * @remarks The server re-reads 15 s before `since`, so a change usually comes back
     *          on the next poll too. A page patches a row it shows only when the change
     *          is newer than the row, but a change to a sale it does not show makes it
     *          re-fetch the page — without this, once more for the repeat.
     */
    dropRepeats(changes) {
        const self = this;

        const fresh = [];
        jQuery.each(changes || [], function (i, change) {
            const version = self.service.getChangeVersion(change);
            const delivered = self.deliveredVersions[change.sale_id];

            if (delivered !== undefined && version <= delivered) {
                return;
            }

            self.deliveredVersions[change.sale_id] = version;
            fresh.push(change);
        });

        return fresh;
    }

    /**
     * @param {string|null} since
     * @returns {Object} The page's params plus `since` when given.
     */
    buildParams(since) {
        const self = this;

        const params = angular.extend({}, self.getParams ? self.getParams() : {});
        if (since) {
            params.since = since;
        }

        return params;
    }

    /** @returns {void} */
    scheduleNext() {
        const self = this;

        self.cancelTimer();
        if (self.stopped) {
            return;
        }

        self.timer = self.service.$timeout(function () {
            self.poll();
        }, self.service.POLL_INTERVAL_MS);
    }

    /** @returns {void} */
    cancelTimer() {
        const self = this;

        if (self.timer) {
            self.service.$timeout.cancel(self.timer);
            self.timer = null;
        }
    }
}

SSSaleChangeFeedService.$inject = ['$timeout', 'SSHttpService', 'SSUserService', 'SSUtilService'];
app.service('SSSaleChangeFeedService', SSSaleChangeFeedService);
